import { useEffect, useState } from "react";
import { Users, ClipboardCheck, TrendingUp, Layers } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Navigate } from "react-router-dom";
import AdminNav from "../components/AdminNav";

const PILLAR_LABEL = {
    "phonemic-awareness": "Phonemic Awareness",
    "phonics": "Phonics",
    "fluency": "Fluency",
    "vocabulary": "Vocabulary",
    "comprehension": "Comprehension",
};
const LEVEL_COLOR = {
    Beginner: "#2D5D4E",
    Developing: "#1F4237",
    Intermediate: "#D47A55",
    Advanced: "#7D2F2F",
    Unknown: "#819888",
};

function Bar({ label, value, total, color = "#2D5D4E" }) {
    const pct = total === 0 ? 0 : Math.round((value / total) * 100);
    return (
        <div className="mb-3">
            <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-[#1C2321] font-medium">{label}</span>
                <span className="text-[#515B57] tabular-nums">{value} <span className="text-[#819888]">({pct}%)</span></span>
            </div>
            <div className="h-2 bg-[#E5E7E2] rounded-full overflow-hidden">
                <div className="h-full" style={{ width: `${pct}%`, background: color }} />
            </div>
        </div>
    );
}

export default function AdminAnalytics() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [clicks, setClicks] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        if (user?.role !== "admin") return;
        api.get("/admin/analytics/placement")
            .then((r) => setData(r.data))
            .catch((e) => setError(e?.response?.data?.detail || "Could not load analytics"));
        api.get("/admin/analytics/clicks").then((r) => setClicks(r.data)).catch(() => {});
    }, [user]);

    if (user && user.role !== "admin") return <Navigate to="/app" replace />;
    if (!data && !error) return <main className="container-page py-12">Loading…</main>;
    if (error) return <main className="container-page py-12 text-[#B24C4C]">{error}</main>;

    return (
        <main className="container-page py-12 fade-up" data-testid="admin-analytics">
            <p className="label-eyebrow mb-2">Admin · Analytics</p>
            <h1 className="text-4xl font-semibold text-[#1C2321] mb-6" style={{ fontFamily: "Outfit" }} data-testid="admin-title">
                Placement funnel
            </h1>
            <AdminNav />
            <p className="text-lg text-[#515B57] max-w-2xl mb-10">
                Where every learner sits in the onboarding funnel. The placement check is the doorway to lessons — track this number.
            </p>

            <div className="grid sm:grid-cols-3 gap-5 mb-12">
                <div className="card-rw p-6" data-testid="metric-total">
                    <div className="flex items-center gap-3 mb-3">
                        <Users className="w-5 h-5 text-[#2D5D4E]" strokeWidth={1.6} />
                        <p className="label-eyebrow">Total learners</p>
                    </div>
                    <p className="text-4xl font-semibold text-[#1C2321]" style={{ fontFamily: "Outfit" }}>{data.total_learners}</p>
                </div>
                <div className="card-rw p-6" data-testid="metric-completed">
                    <div className="flex items-center gap-3 mb-3">
                        <ClipboardCheck className="w-5 h-5 text-[#2D5D4E]" strokeWidth={1.6} />
                        <p className="label-eyebrow">Completed placement</p>
                    </div>
                    <p className="text-4xl font-semibold text-[#1C2321]" style={{ fontFamily: "Outfit" }}>{data.completed}</p>
                    <p className="text-sm text-[#819888] mt-1">{data.completion_rate_pct}% of all learners</p>
                </div>
                <div className="card-rw p-6" data-testid="metric-of-started">
                    <div className="flex items-center gap-3 mb-3">
                        <TrendingUp className="w-5 h-5 text-[#D47A55]" strokeWidth={1.6} />
                        <p className="label-eyebrow">Of those who started</p>
                    </div>
                    <p className="text-4xl font-semibold text-[#1C2321]" style={{ fontFamily: "Outfit" }}>{data.completion_of_started_pct}%</p>
                    <p className="text-sm text-[#819888] mt-1">finished the check</p>
                </div>
            </div>

            <section className="grid md:grid-cols-2 gap-6">
                <div className="card-rw p-7" data-testid="by-level">
                    <div className="flex items-center gap-2 mb-5">
                        <Layers className="w-5 h-5 text-[#2D5D4E]" strokeWidth={1.6} />
                        <h2 className="text-xl font-medium" style={{ fontFamily: "Outfit" }}>Placement results · level</h2>
                    </div>
                    {Object.entries(data.by_level).length === 0 ? (
                        <p className="text-[#515B57]">No completed placements yet.</p>
                    ) : (
                        Object.entries(data.by_level)
                            .sort((a, b) => b[1] - a[1])
                            .map(([lvl, n]) => (
                                <Bar key={lvl} label={lvl} value={n} total={data.completed} color={LEVEL_COLOR[lvl] || "#2D5D4E"} />
                            ))
                    )}
                </div>
                <div className="card-rw p-7" data-testid="by-pillar">
                    <div className="flex items-center gap-2 mb-5">
                        <Layers className="w-5 h-5 text-[#2D5D4E]" strokeWidth={1.6} />
                        <h2 className="text-xl font-medium" style={{ fontFamily: "Outfit" }}>Recommended starting pillar</h2>
                    </div>
                    {Object.entries(data.by_pillar).length === 0 ? (
                        <p className="text-[#515B57]">No completed placements yet.</p>
                    ) : (
                        Object.entries(data.by_pillar)
                            .sort((a, b) => b[1] - a[1])
                            .map(([pid, n]) => (
                                <Bar key={pid} label={PILLAR_LABEL[pid] || pid} value={n} total={data.completed} color="#2D5D4E" />
                            ))
                    )}
                </div>
            </section>

            {/* Tutoring cross-sell clicks + A/B */}
            {clicks && (
                <section className="mt-10 grid md:grid-cols-2 gap-6" data-testid="click-analytics">
                    <div className="card-rw p-7">
                        <h2 className="text-xl font-medium mb-5" style={{ fontFamily: "Outfit" }}>Tutoring clicks by surface</h2>
                        <p className="text-sm text-[#819888] mb-4">Total outbound clicks: <span className="font-medium text-[#1C2321]">{clicks.total}</span></p>
                        {Object.keys(clicks.by_surface).length === 0 ? (
                            <p className="text-[#515B57]">No outbound clicks yet.</p>
                        ) : (
                            Object.entries(clicks.by_surface).sort((a,b)=>b[1]-a[1]).map(([surface, n]) => (
                                <Bar key={surface} label={surface} value={n} total={clicks.total} color="#D47A55" />
                            ))
                        )}
                    </div>
                    <div className="card-rw p-7">
                        <h2 className="text-xl font-medium mb-5" style={{ fontFamily: "Outfit" }}>Dashboard A/B (top vs. bottom)</h2>
                        {Object.keys(clicks.by_variant).length === 0 ? (
                            <p className="text-[#515B57]">No variant data yet — the dashboard tutoring card randomizes per-user.</p>
                        ) : (
                            Object.entries(clicks.by_variant).sort((a,b)=>b[1]-a[1]).map(([k, n]) => (
                                <Bar key={k} label={k} value={n} total={clicks.total} color={k.endsWith("top") ? "#2D5D4E" : "#D47A55"} />
                            ))
                        )}
                    </div>
                </section>
            )}
        </main>
    );
}
